import { Controller } from "@hotwired/stimulus";

// Captures onboarding step funnel events for PostHog.
// Wired by adding data-controller="onboarding-step" on the step's root
// element with step-number and step-name values. flex-signup is read from
// a value attribute and copied into every event.
//
// - on connect:     fires onboarding_step_viewed
// - on #complete:   fires onboarding_step_completed (call from form submit
//                   or any explicit advance action)
// - on pagehide:    fires onboarding_step_abandoned if #complete was not
//                   called for this step. Uses pagehide (not beforeunload)
//                   because pageleave-style telemetry survives SPAs better.
export default class extends Controller {
  static values = {
    stepNumber: Number,
    stepName: String,
    flexSignup: Boolean,
  };

  connect() {
    this.completed = false;
    this.capture("onboarding_step_viewed");

    this.boundLeaveHandler = this.handleLeave.bind(this);
    window.addEventListener("pagehide", this.boundLeaveHandler);
    document.addEventListener("turbo:before-visit", this.boundLeaveHandler);
  }

  disconnect() {
    if (this.boundLeaveHandler) {
      window.removeEventListener("pagehide", this.boundLeaveHandler);
      document.removeEventListener(
        "turbo:before-visit",
        this.boundLeaveHandler
      );
    }
  }

  complete() {
    this.completed = true;
    // complete() is wired to a form submit / advance action that navigates the
    // page (logo form POST, "Start a Collab" submit). A queued XHR capture is
    // aborted by that navigation — which is why onboarding_step_viewed fired but
    // onboarding_step_completed never did. sendBeacon survives the transition.
    this.capture("onboarding_step_completed", { transport: "sendBeacon" });
  }

  handleLeave() {
    if (this.completed) return;
    this.completed = true; // guard against duplicate fires
    this.capture("onboarding_step_abandoned", { transport: "sendBeacon" });
  }

  capture(eventName, options = {}) {
    if (typeof window.posthog === "undefined") return;
    window.posthog.capture(
      eventName,
      {
        step_number: this.stepNumberValue || null,
        step_name: this.stepNameValue || null,
        flex_signup: this.hasFlexSignupValue ? this.flexSignupValue : false,
      },
      options
    );
  }
}
